<template>
   <AccountProfileField
      v-if="preferredLocaleName"
      :title="field.label"
      :fieldNotice="field.fieldNotice"
      :details="preferredLocaleName"
   >
      <!-- Using slot instead of details string to render the SimpleSelect component -->
      <template #details>
         <SimpleSelect
            v-if="localeStore.siteSupportedLocales.length > 1"
            data-cy="account-profile-locale-preference"
            :options="localeList"
            :label="$t('account.preferred_locale.label')"
            :selectedOption="preferredLocale"
            @option-selected="onPrefLocaleSelect"
         />
      </template>
   </AccountProfileField>
</template>

<script setup lang="ts">
import UsersApi from "@/data/api-layer/usersApi";
import { useLocaleStore } from "@/stores/localeStore";
import { useUserStore } from "@/stores/userStore";
import type { AccountProfileField } from "@/components/account/accountTypes";

// Declarations
// -----------------------------------------
const $t = useI18n().t;
const localeStore = useLocaleStore();
const userStore = useUserStore();

const props = defineProps<{
   field: AccountProfileField;
}>();

// State
const preferredLocale = ref(userStore.user.locale?.id);

// Computed
// -----------------------------------------
const preferredLocaleName = computed(() => {
   const locale = localeStore.siteSupportedLocales.find((locale) => locale.id === preferredLocale.value);
   return locale ? locale.name : "";
});

const localeList = computed(() => {
   return localeStore.siteSupportedLocales.map((locale) => {
      return {
         value: locale.id,
         label: locale.name,
      };
   });
});

// Methods
// -----------------------------------------
async function onPrefLocaleSelect(newLocaleValue: string) {
   if (localeList.value.find((locale) => locale.value === newLocaleValue) === undefined) {
      console.error("Invalid locale selected");
      return;
   }
   preferredLocale.value = newLocaleValue;
   try {
      await UsersApi.updateUserFields({ locale_id: newLocaleValue });
   } catch (e) {
      console.error("Error while updating the user preferred locale", e);
   }
}
</script>
